import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'
import { createQueryParams } from '@/common/utils/transformFilters'
import { LIMIT_CODE } from '@/common/constants/responseCodes'
import { open } from '@/store/modalsSlice'
import { LIMIT_MODAL } from '@/common/constants/modals'
import {
  resetAllFilters,
  resetFilterToDefaultByInputs,
  resetToDefaultFilter,
  setCurrentLayoutType,
  setFiltersParams,
  setIsInfiniteScroll,
  setIsLoadingItems,
  setItems,
  setPagesMap,
  updateFiltersParams,
  updatePaginationParams,
} from '@/store/reducers/SearchReducers'
import { searchInitialState } from '@/store/states/SearchInitialState'
import {
  fetchFiltersConfigPending,
  fetchFiltersConfigReject,
  fetchFiltersConfigSuccess,
  fetchItemsPending,
  fetchItemsRejected,
  fetchItemsSuccess,
  searchHydrate,
} from '@/store/actions/searchActions'
import { SEARCH_APPS } from '@/common/constants/search'
import { fetchFiltersConfig } from '@/store/thunks/searchThunks'

export const fetchApps = createAsyncThunk(
  'apps/fetchApps',
  async (
    { withInfiniteScroll = false, request, params = {} },
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      if (!withInfiniteScroll) {
        const { setApps } = appsActions
        dispatch(setApps([]))
      }

      const filters = getState().apps.filtersParams

      const queryMap = {
        ...params,
        ...createQueryParams(filters),
      }
      const { apps, pagination } = await request(queryMap)

      return {
        items: apps,
        pagination: pagination || { pages: {}, results: {} },
        withInfiniteScroll: withInfiniteScroll,
      }
    } catch (e) {
      const headers = e?.response?.headers ?? {}
      const error = e.data?.error ?? {}

      if (error?.code === LIMIT_CODE) {
        await dispatch(open(LIMIT_MODAL))
      }

      return rejectWithValue({ ...headers, ...error })
    }
  }
)

export const fetchDetailsApp = createAsyncThunk(
  'apps/fetchDetailsApp',
  async ({ params, request }, { rejectWithValue }) => {
    try {
      const data = await request(params)

      return {
        ...data,
        app: {
          ...data.app,
          isTranslate: false,
        },
      }
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

export const appsSlice = createSlice({
  name: 'apps',
  initialState: {
    ...searchInitialState,
    selectedApp: null,
    similarApps: [],
    isLoadingDetails: true,
  },
  reducers: {
    setSelectedApp: (state, { payload }) => {
      state.selectedApp = payload
    },
    setCurrentLayoutType: setCurrentLayoutType,
    setIsLoadingApps: setIsLoadingItems,
    setIsInfiniteScroll: setIsInfiniteScroll,
    updatePaginationParamsApps: updatePaginationParams,
    setApps: setItems,
    setPagesMapApps: setPagesMap,
    updateFiltersParamsApps: updateFiltersParams,
    setFiltersParamsApps: setFiltersParams,
    resetToDefaultFilterApps: resetToDefaultFilter,
    resetFilterToDefaultByInputsApps: resetFilterToDefaultByInputs,
    resetAllFiltersApps: resetAllFilters,
  },
  extraReducers: {
    [HYDRATE]: (state, action) => {
      const { apps } = action.payload

      if (apps.selectedApp || apps.similarApps.length) {
        state.selectedApp = apps.selectedApp
        state.similarApps = apps.similarApps
      }
      searchHydrate({ state, action, slice: SEARCH_APPS })
    },
    [fetchDetailsApp.pending]: (state) => {
      state.isLoadingDetails = true
    },
    [fetchDetailsApp.fulfilled]: (state, { payload }) => {
      state.isLoadingDetails = false
      state.selectedApp = payload.app
      state.similarApps = payload.similar
    },
    [fetchDetailsApp.rejected]: (state) => {
      state.isLoadingDetails = false
    },
    [fetchApps.pending]: fetchItemsPending,
    [fetchApps.fulfilled]: fetchItemsSuccess,
    [fetchApps.rejected]: fetchItemsRejected,
    [fetchFiltersConfig.pending]: (state, action) => {
      if (action.payload === SEARCH_APPS) {
        fetchFiltersConfigPending(state, action)
      }
    },
    [fetchFiltersConfig.fulfilled]: (state, action) => {
      if (action.payload.sliceName === SEARCH_APPS) {
        fetchFiltersConfigSuccess(state, action)
      }
    },
    [fetchFiltersConfig.rejected]: (state, action) => {
      if (action.payload === SEARCH_APPS) {
        fetchFiltersConfigReject(state, action)
      }
    },
  },
})

export const appsActions = appsSlice.actions

export default appsSlice.reducer
